/**
 * NGAP & cotations — Moteur de calcul de la nomenclature
 * Accessible superadmin (rbac/abac gated, opens to all in dev)
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calculator, Search, Plus, Trash2, Info, FileText, ChevronRight, Euro } from "lucide-react";
import { loadAllActs, MODIFICATEURS, NGAP_RULES, calculateActPrice, CATEGORY_LABEL, CATEGORY_TONE, formatAmountMad, type NgapAct, type NgapModifier } from "@/lib/ngap-catalogue";

export const Route = createFileRoute("/referential/ngap")({
  component: NGAPPricing,
});

function NGAPPricing() {
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<"all" | NgapAct["category"]>("all");
  const [cart, setCart] = useState<Array<{ act: NgapAct; mods: string[] }>>([]);
  const [showRules, setShowRules] = useState(false);
  // The catalogue is loaded on demand: 15 lazy chunks, one per specialty.
  const [acts, setActs] = useState<NgapAct[]>([]);

  useEffect(() => {
    let cancelled = false;
    loadAllActs()
      .then((loaded) => { if (!cancelled) setActs(loaded); })
      .catch(() => { if (!cancelled) setActs([]); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    return acts.filter((a) => {
      if (filterCategory !== "all" && a.category !== filterCategory) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return a.code.toLowerCase().includes(s) ||
             a.label.toLowerCase().includes(s) ||
             (a.description ?? "").toLowerCase().includes(s);
    });
  }, [acts, search, filterCategory]);

  function addToCart(act: NgapAct) {
    setCart((c) => [...c, { act, mods: [] }]);
  }
  function removeFromCart(i: number) {
    setCart((c) => c.filter((_, j) => j !== i));
  }
  function toggleMod(i: number, modCode: string) {
    setCart((c) => c.map((item, j) => {
      if (j !== i) return item;
      const has = item.mods.includes(modCode);
      return { ...item, mods: has ? item.mods.filter((m) => m !== modCode) : [...item.mods, modCode] };
    }));
  }

  const cartTotal = cart.reduce((s, item) => s + (calculateActPrice(item.act, item.mods) ?? 0), 0);
  const cartTotalMad = cartTotal;

  const categories: Array<{ id: "all" | NgapAct["category"]; label: string; count: number }> = [
    { id: "all", label: "Tous", count: acts.length },
    { id: "consultation", label: "Consultations", count: acts.filter((a) => a.category === "consultation").length },
    { id: "visite", label: "Visites", count: acts.filter((a) => a.category === "visite").length },
    { id: "acte-technique", label: "Actes techniques", count: acts.filter((a) => a.category === "acte-technique").length },
    { id: "biologie", label: "Biologie", count: acts.filter((a) => a.category === "biologie").length },
    { id: "imagerie", label: "Imagerie", count: acts.filter((a) => a.category === "imagerie").length },
    { id: "sage-femme", label: "Sage-femme", count: acts.filter((a) => a.category === "sage-femme").length },
  ];

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Calculator className="h-5 w-5" /> NGAP & cotations
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Nomenclature Générale des Actes Professionnels · {acts.length} actes · {MODIFICATEURS.length} modificateurs
        </p>
      </div>

      <Tabs defaultValue="acts">
        <TabsList>
          <TabsTrigger value="acts"><FileText className="h-3.5 w-3.5 mr-1" /> Actes ({acts.length})</TabsTrigger>
          <TabsTrigger value="modificateurs"><Plus className="h-3.5 w-3.5 mr-1" /> Modificateurs ({MODIFICATEURS.length})</TabsTrigger>
          <TabsTrigger value="rules"><Info className="h-3.5 w-3.5 mr-1" /> Règles ({NGAP_RULES.length})</TabsTrigger>
        </TabsList>

        {/* ACTS TAB */}
        <TabsContent value="acts" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-[1fr_320px]">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Rechercher (code, libellé, description)..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                </div>
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                {categories.map((c) => (
                  <Button
                    key={c.id}
                    size="sm"
                    variant={filterCategory === c.id ? "default" : "outline"}
                    onClick={() => setFilterCategory(c.id)}
                    className="text-xs"
                  >
                    {c.label} <span className="ml-1 text-[10px] opacity-70">({c.count})</span>
                  </Button>
                ))}
              </div>

              <div className="space-y-1.5">
                {filtered.map((act) => (
                  <Card key={act.code}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary font-mono text-xs font-bold">
                          {act.priceLetter ?? "—"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-bold">{act.code}</span>
                            <span className="font-medium text-sm">{act.label}</span>
                            <Badge variant={CATEGORY_TONE[act.category]} className="text-[10px]">{CATEGORY_LABEL[act.category]}</Badge>
                          </div>
                          <div className="mt-0.5 text-xs text-muted-foreground">{act.description}</div>
                          <div className="mt-0.5 text-[11px] text-muted-foreground">
                            Coef {act.coefficient} · Base {formatAmountMad(act.baseAmountMad)} · Prix {formatAmountMad(act.baseAmountMad === null ? null : act.baseAmountMad * act.coefficient)}
                            {act.pricingStatus !== "priced" && (
                              <span className="ml-2 text-amber-400">
                                prix {act.pricingStatus === "provisional" ? "provisoire" : "non établi"} — lettre {act.priceLetter ?? "—"} (confiance {act.pricingConfidence})
                              </span>
                            )}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => addToCart(act)}>
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {filtered.length === 0 && (
                  <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground">
                    Aucun acte ne correspond
                  </div>
                )}
              </div>
            </div>

            {/* CART */}
            <Card className="sticky top-4 h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="h-4 w-4" />
                  Facture en cours
                </CardTitle>
                <CardDescription>{cart.length} acte{cart.length > 1 ? "s" : ""}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {cart.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/10 p-4 text-center text-xs text-muted-foreground">
                    Cliquez sur + pour ajouter des actes
                  </div>
                ) : (
                  <>
                    {cart.map((item, i) => {
                      const price = calculateActPrice(item.act, item.mods);
                      return (
                        <div key={i} className="rounded-md border border-white/5 bg-white/[0.02] p-2 space-y-1">
                          <div className="flex items-center justify-between">
                            <div className="text-xs">
                              <span className="font-mono font-bold">{item.act.code}</span>
                              <span className="ml-1.5 text-muted-foreground">{item.act.label}</span>
                            </div>
                            <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => removeFromCart(i)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-1 flex-wrap">
                            {MODIFICATEURS.map((m: NgapModifier) => (
                              <Button
                                key={m.code}
                                size="sm"
                                variant={item.mods.includes(m.code) ? "default" : "outline"}
                                onClick={() => toggleMod(i, m.code)}
                                className="h-5 text-[10px] px-1.5"
                              >
                                {m.code} ×{m.factor}
                              </Button>
                            ))}
                          </div>
                          <div className="text-right text-sm font-mono font-bold">
                            {(price ?? 0).toFixed(2)} MAD
                          </div>
                        </div>
                      );
                    })}
                    <div className="border-t border-white/10 pt-2 mt-2 flex items-center justify-between">
                      <span className="text-sm font-medium">Total</span>
                      <span className="text-lg font-bold text-emerald-400">{cartTotalMad.toFixed(2)} MAD</span>
                    </div>
                    <Button className="w-full" size="sm">
                      <Euro className="h-3.5 w-3.5 mr-1" /> Générer la FSE
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* MODIFICATEURS TAB */}
        <TabsContent value="modificateurs" className="space-y-2">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {MODIFICATEURS.map((m: NgapModifier) => (
              <Card key={m.code}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="font-mono font-bold text-base">{m.code}</Badge>
                    <span className="text-sm font-medium">{m.label}</span>
                    <span className="ml-auto text-sm font-bold text-amber-400">×{m.factor}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{m.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* RULES TAB */}
        <TabsContent value="rules" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Règles de cotation NGAP</CardTitle>
              <CardDescription>Ces règles sont codées dans le moteur de calcul (calculate_ngap)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {NGAP_RULES.map((r, i) => (
                <div key={i} className="flex items-start gap-2 rounded-md border border-white/5 bg-white/[0.02] p-2 text-sm">
                  <span className="font-mono text-xs text-muted-foreground w-6 shrink-0">{i + 1}.</span>
                  <span>{r}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
